import { IdentityAppAccessToken, IdentityJwtUser, IdentityMachineToken } from '../auth';
import { IdentityProviders } from '../identity';
import { Moderator, PublicModeratorForUser } from '../streamer';
import { PublicRemoteUser, RemoteUser } from './remoteUser';

export interface User {
  id: number;
  isAdmin: boolean;
  isBot: boolean;
  createdAt: Date;
  modifiedAt: Date;
  language: string;
  authId?: string | null;
  identityId?: string | null;
  username?: string | null;
  displayName?: string | null;

  streamer: {
    id: number;
  } | null;

  remoteUsers?: RemoteUser[];
  modForStreamers?: Moderator[];
}

export type ApiUser = User & {
  providers: UserProviders;
  remoteUsers: RemoteUser[];
} & Required<Pick<User, 'streamer'>>;

export type AuthenticatedWebUser = ApiUser & { ttyp: 'web' } & { allowRoles: true };

export type AuthenticatedStreamer = ApiUser & { ttyp: 'streamer' } & { allowRoles: false };

export type AuthenticatedApiUser = ApiUser & IdentityJwtUser & { allowRoles: boolean };

export type AuthenticatedMachine = IdentityMachineToken & { allowRoles: false };

export type AuthenticatedApp = IdentityAppAccessToken & { allowRoles: false };

export type AuthenticatedContext =
  | AuthenticatedWebUser
  | AuthenticatedApiUser
  | AuthenticatedStreamer
  | AuthenticatedMachine
  | AuthenticatedApp;

export type UserProviders = {
  [K in IdentityProviders]?: RemoteUser;
};

export type PublicUser = Pick<User, 'id' | 'language' | 'streamer' | 'identityId' | 'username'> & {
  modForStreamers: PublicModeratorForUser[];
  providers: UserProviders;
};

export interface ExtUser extends Pick<User, 'id' | 'language' | 'streamer'> {
  username: PublicRemoteUser['username'];
}

export type UpdateUser = Partial<Pick<User, 'language' | 'username' | 'displayName'>>;

export type UserModerator = Pick<PublicUser, 'id'> & { providers: UserProviders };

export const apiToPublicUser = (user: ApiUser): PublicUser => ({
  id: user.id,
  identityId: user.identityId,
  providers: user.providers,
  language: user.language,
  streamer: user.streamer ? { id: user.streamer.id } : null,
  modForStreamers: (user.modForStreamers as never) || [],
});

export const apiToUserModerator = (user: ApiUser): UserModerator => ({
  id: user.id,
  providers: user.providers,
});

export interface Language {
  value: string;
  name: string;
}

export const LanguageCodes = ['cs', 'de', 'en', 'es', 'fr', 'ja', 'pt-BR', 'ru', 'zh-CN'] as const;

/**
 * Stored when a user has not picked a language, meaning the UI locale is
 * detected from the browser's Accept-Language header instead.
 */
export const AUTOMATIC_LANGUAGE = '';

export const SUPPORTED_LANGUAGES: Language[] = [
  { value: 'cs', name: 'Czech' },
  { value: 'de', name: 'German' },
  { value: 'en', name: 'English' },
  { value: 'es', name: 'Spanish' },
  { value: 'fr', name: 'French' },
  { value: 'ja', name: 'Japanese' },
  { value: 'pt-BR', name: 'Portuguese (Brazil)' },
  { value: 'ru', name: 'Russian' },
  { value: 'zh-CN', name: 'Chinese' },
];
